import { useState, useEffect, useCallback } from 'react';
import { parseDuration, getDurationRange, findDurationFormat, convertDurationsToMonths } from '../utils/durationUtils';
import { fetchWithCache } from '../utils/apiCache';

const useLoanCalculator = (appConfig) => {
  const { productApiUrl: productUrl, productId: productName , siteCurrency, onlyApplyButton, fullDisclosureMode, costBreakdownEnabled} = appConfig;

  const [amountSelected, setAmountSelected] = useState(0);
  const [durationSelected, setDurationSelected] = useState(0);
  const [currency, setCurrency] = useState(siteCurrency);
  const [apr, setApr] = useState(null);
  const [monthlyRepayments, setMonthlyRepayments] = useState(null);
  const [selectDurationFormatted, setSelectDurationFormatted] = useState(null);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amountTouched, setAmountTouched] = useState(false);
  const [durationTouched, setDurationTouched] = useState(false);

  useEffect(() => {
    if (onlyApplyButton) {
      setLoading(false);
      setError(null);
      setProductData({
        amountRange: [5000, 10000],
        durationRange: ["1M", "12M"],
        defaultAmount: 5000,
        defaultDuration: "6M",
        currency: siteCurrency || '€'
      });
      setCurrency(siteCurrency || '€');
      setAmountSelected(5000);
      setDurationSelected(6);
      setSelectDurationFormatted("6M");
      return;
    }

    const fetchProductData = async () => {
      try {
        setLoading(true);

        const cacheKey = `${productName}`;

        const data = await fetchWithCache(
          `${productUrl}offer/?offerId=${productName}`,
          cacheKey,
          'productData'
        );

        if (!data?.amountRange?.length) {
          throw new Error('Invalid product data');
        }

        setProductData(data);
        setCurrency(data.currency || '€');
        setApr(data.apr ?? null);

        if (!fullDisclosureMode) {
          setAmountSelected(data.defaultAmount);

          const defaultDurationObj = parseDuration(data.defaultDuration);
          setDurationSelected(
            defaultDurationObj.unit === "D"
              ? defaultDurationObj.value / 30
              : defaultDurationObj.value
          );

          setSelectDurationFormatted(data.defaultDuration);
        }

        const repaymentData = await fetchWithCache(
          `${productUrl}calc/monthly-repayment?offerId=${productName}`,
          cacheKey,
          'repaymentData'
        );

        if (!repaymentData) {
          throw new Error('Failed to load repayment data');
        }

        setMonthlyRepayments(repaymentData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching API data:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productName, productUrl, onlyApplyButton, siteCurrency, fullDisclosureMode]);


  const calculateInstallment = useCallback(() => {
    try {
      if (!monthlyRepayments || !amountSelected || !selectDurationFormatted) return null;

      const data = monthlyRepayments[amountSelected];
      if (!data) return null;

      const entry = data[selectDurationFormatted];
      if (entry == null) return null;

      const isObject = typeof entry === 'object';
      const installmentAmount = isObject ? (entry.installment ?? 0) : entry;

      if (!fullDisclosureMode && !costBreakdownEnabled) {
        return {
          installmentAmount,
          principal: amountSelected
        };
      }

      return {
        installmentAmount,
        principal: amountSelected,
        totalAmount: isObject ? (entry.total ?? null) : null,
        firstInstallment: isObject ? (entry.firstInstallment ?? null) : null,
        lastInstallment: isObject ? (entry.lastInstallment ?? null) : null,
        apr: isObject ? (entry.apr ?? null) : null,
        aprc: isObject ? (entry.aprc ?? null) : null,
        loanOriginationFee: isObject ? (entry.loanOriginationFee ?? null) : null,
        contractFee: isObject ? (entry.contractFee ?? null) : null,
        totalInterest: isObject ? (entry.totalInterest ?? null) : null
      };
    } catch (error) {
      console.error("Error calculating installment:", error);
      return null;
    }
  }, [amountSelected, monthlyRepayments, selectDurationFormatted, fullDisclosureMode, costBreakdownEnabled]);


  const handleAmountChange = useCallback((value) => {
    setAmountSelected(value);
    setAmountTouched(true);
    if (!productData) {
      return;
    }

    const {
      durationRange,
      amountRangeCombinationMethod,
      hasAmountRangeCombination,
      amountRangeCombination,
      upperConstant,
      lowerConstant
    } = productData;

    const validDurations = getDurationRange({
      durationRange,
      method: amountRangeCombinationMethod,
      hasCombination: hasAmountRangeCombination,
      combination: amountRangeCombination,
      upperConstant,
      lowerConstant,
      amountSelected: value
    });

    const durations = convertDurationsToMonths(validDurations);

    if (productData.productId?.toLowerCase().includes('denmark')) {
      if (!durations.length) return;

      const newDuration = Math.max(...durations);
      setDurationSelected(newDuration);
      const newDurationFormatted = findDurationFormat(durationRange, newDuration);
      setSelectDurationFormatted(newDurationFormatted);
      return;
    }

    if (fullDisclosureMode && !durationTouched) {
      return;
    }

    if (!durations.includes(durationSelected)) {
      const newDuration = Math.max(...durations);
      setDurationSelected(newDuration);
      const newDurationFormatted = findDurationFormat(durationRange, newDuration);
      setSelectDurationFormatted(newDurationFormatted);
    }
  }, [productData, durationSelected, durationTouched, fullDisclosureMode]);

  const handleDurationChange = useCallback((value) => {
    setDurationSelected(value);
    setDurationTouched(true);

    if (productData?.durationRange) {
      const newDurationFormatted = findDurationFormat(productData.durationRange, value);
      setSelectDurationFormatted(newDurationFormatted);
    }
  }, [productData]);

  const fetchSecciDocument = useCallback(async () => {
    try {
      const secciUrl = `${productUrl}documents/secci?amount=${amountSelected}&duration=${durationSelected*30}&offer=${productName}`;
      // Only use essential parameters in cache key
      const secciCacheKey = `${productName}_${amountSelected}_${durationSelected*30}_secci`;

      const data = await fetchWithCache(
        secciUrl,
        secciCacheKey,
        'secciDocuments'
      );

      return data;
    } catch (error) {
      console.error("Error fetching SECCI:", error);
      throw error;
    }
  }, [productUrl, amountSelected, durationSelected, productName]);


  const getValidDurations = useCallback(() => {
    if (!productData) return [];

    const {
      durationRange,
      amountRangeCombinationMethod,
      hasAmountRangeCombination,
      amountRangeCombination,
      upperConstant,
      lowerConstant
    } = productData;

    return getDurationRange({
      durationRange,
      method: amountRangeCombinationMethod,
      hasCombination: hasAmountRangeCombination,
      combination: amountRangeCombination,
      upperConstant,
      lowerConstant,
      amountSelected
    });
  }, [productData, amountSelected, fullDisclosureMode]);

  return {
    loading,
    error,
    productData,
    amountSelected,
    durationSelected,
    currency,
    calculateInstallment,
    handleAmountChange,
    handleDurationChange,
    fetchSecciDocument,
    getValidDurations,
    amountTouched,
    durationTouched,
    apr,
  };
};

export default useLoanCalculator;
